import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Grid from '@nubank/nuds-web/components/Grid/Grid';
import Box from '@nubank/nuds-web/components/Box/Box';
import Link from '@nubank/nuds-web/components/Link/Link';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';

import Carousel from 'components/Carousel/Carousel';
import Slides from 'components/Slides';
import { SlidesContextProvider } from 'components/Slides/SlidesContext';

const ResponsiveImages = dynamic(import('components/ResponsiveImage/ResponsiveImages'), { ssr: false });

function SectionFeatures({ index }) {
  const { formatMessage } = useContext(NuDsContext);
  const carouselItems = formatMessage({ id: `cms.cardPage.cardCardsArea[${index}].carousel` });

  const [currentStep, setCurrentStep] = useState(1);

  const handleChangeSlide = step => {
    setCurrentStep(step);
  };

  return (
    <SlidesContextProvider
      value={{
        currentStep,
        updateSlide: handleChangeSlide,
        slides: carouselItems,
      }}
    >
      <Box
        tag="section"
        display="flex"
        paddingVertical={{ xs: '12x', md: '20x', lg: '32x' }}
        backgroundColor="primary.default"
        minHeight={{ md: 'calc(100vh - 4rem)', lg: 'calc(100vh - 3.5rem)' }}
      >
        <Grid
          rowGap={{ md: '12x' }}
          alignItems={{ lg: 'center' }}
        >
          <Grid.Child
            gridColumn={{ xs: '1 / span 4', md: '1 / span 6', lg: '2 / span 3' }}
          >
            <Box display={{ xs: 'none', md: 'block' }}>
              <Carousel
                currentStep={currentStep}
                onStepChanged={handleChangeSlide}
                showControls={false}
                renderOnlySelected
              >
                {carouselItems.map(({ imagePath, imageAlt }) => (
                  <ResponsiveImages
                    key={imagePath}
                    alt={imageAlt}
                    loading="lazy"
                    fileName={imagePath}
                  />
                ))}
              </Carousel>
            </Box>
          </Grid.Child>
          <Grid.Child
            gridColumn={{ xs: '1 / span 4', md: '1 / span 6', lg: '7 / span 5' }}
          >
            <Slides
              carouselProps={{
                controlsProps: {
                  variant: 'basic',
                  styleVariant: 'white',
                },
              }}
              renderSlide={({ title }, slideIndex) => (
                <Fragment key={slideIndex}>
                  <Typography
                    variant="title"
                    color="white"
                    marginBottom="4x"
                  >
                    {title}
                  </Typography>
                  {formatMessage({
                    id: `cms.cardPage.cardCardsArea[${index}].carousel[${slideIndex}].description`,
                  }, {
                    p: props => <Typography variant="paragraph2" color="white" {...props} />,
                    a: props => (
                      <Link
                        color="white"
                        variant="action"
                        {...props}
                      />
                    ),
                  })}
                </Fragment>
              )}
            />
          </Grid.Child>
        </Grid>
      </Box>
    </SlidesContextProvider>
  );
}

SectionFeatures.propTypes = {
  index: PropTypes.number.isRequired,
};

export default React.memo(SectionFeatures);
